import React, { useEffect, useState } from 'react';
import { X, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  voiceRepeats: number;
  setVoiceRepeats: (n: number) => void;
  soundEnabled: boolean;
  setSoundEnabled: (b: boolean) => void;
  currentTheme: string;
  setTheme: (theme: string) => void;
  onResetGame: () => void;
  autoMark: boolean;
  setAutoMark: (b: boolean) => void;
  overlayFontSize?: number;
  setOverlayFontSize?: (size: number) => void;
  headerColor?: string;
  setHeaderColor?: (color: any) => void;
  useSameColorForHighlight?: boolean;
  setUseSameColorForHighlight?: (val: boolean) => void;
  highlightColor?: string;
  setHighlightColor?: (color: any) => void;
  hideCalled?: boolean;
  setHideCalled?: (val: boolean) => void;
  floatingEmojiDensity?: number;
  setFloatingEmojiDensity?: (val: number) => void;
}

const themes = [
  { id: 'bubblegum', label: 'Bubblegum', emoji: '\ud83e\udee7' },
  { id: 'neon-glow', label: 'Neon Glow', emoji: '\ud83c\udf03' },
  { id: 'tropical', label: 'Tropical', emoji: '\ud83c\udf34' },
  { id: 'candy', label: 'Candy', emoji: '\ud83c\udf6d' },
  { id: 'space', label: 'Space', emoji: '\ud83d\ude80' },
  { id: 'retro', label: 'Retro', emoji: '\ud83d\udd79\ufe0f' },
  { id: 'halloween', label: 'Halloween', emoji: '\ud83c\udf83' },
  { id: 'christmas', label: 'Christmas', emoji: '\ud83c\udf84' },
  { id: 'valentine', label: 'Valentine', emoji: '\ud83d\udc96' },
];

const headerColors = [
  { id: 'gray', label: 'Gray', class: 'bg-slate-700' },
  { id: 'blue', label: 'Blue', class: 'bg-blue-600' },
  { id: 'red', label: 'Red', class: 'bg-red-600' },
  { id: 'dark', label: 'Dark', class: 'bg-zinc-900' },
];

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen, onClose,
  voiceRepeats, setVoiceRepeats,
  soundEnabled, setSoundEnabled,
  currentTheme, setTheme,
  onResetGame,
  overlayFontSize = 65, setOverlayFontSize,
  headerColor = 'dark', setHeaderColor,
  useSameColorForHighlight = true, setUseSameColorForHighlight,
  highlightColor = 'dark', setHighlightColor,
  hideCalled = false, setHideCalled,
  floatingEmojiDensity = 12, setFloatingEmojiDensity,
}) => {
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    if (!isOpen) setConfirmReset(false);
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', bounce: 0.2 }}
            className="modal bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden relative"
            onClick={e => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-4 right-4 p-2 text-pink-400 hover:text-pink-600" aria-label="Close settings">
              <X size={22} />
            </button>

            {/* Scrollable content area */}
            <div className="p-6 overflow-y-auto max-h-[90vh]">
              <h2 className="text-2xl font-bold mb-6 text-pink-900">Settings</h2>

              {/* Theme */}
              <div className="mb-6">
                <div className="text-sm font-semibold text-pink-700 mb-2">THEME</div>
                <div className="grid grid-cols-3 gap-2">
                  {themes.map(t => (
                    <button key={t.id} onClick={() => setTheme(t.id)} className={`p-3 rounded-2xl text-left transition-all bouncy-btn ${currentTheme === t.id ? 'ring-2 ring-pink-500 bg-pink-50' : 'bg-pink-50/50 hover:bg-pink-100'}`}>
                      <div className="text-2xl mb-0.5">{t.emoji}</div>
                      <div className="text-xs font-medium text-pink-800">{t.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Floating Emoji Density */}
              {setFloatingEmojiDensity && (
                <div className="mb-6">
                  <div className="flex justify-between text-sm font-semibold text-pink-700 mb-2">
                    <span>FLOATING EMOJI DENSITY</span>
                    <span className="font-mono">{floatingEmojiDensity}</span>
                  </div>
                  <input type="range" min={6} max={28} step={1} value={floatingEmojiDensity} onChange={e => setFloatingEmojiDensity(parseInt(e.target.value))} className="w-full accent-pink-500" />
                  <div className="text-[10px] text-pink-500 mt-1">Higher = more emojis floating</div>
                </div>
              )}

              {/* Header (BINGO row) Color */}
              {setHeaderColor && (
                <div className="mb-6">
                  <div className="text-sm font-semibold text-pink-700 mb-2">TOP ROW COLOR (BINGO)</div>
                  <div className="flex gap-2">
                    {headerColors.map(c => (
                      <button key={c.id} onClick={() => setHeaderColor(c.id)} className={`flex-1 h-10 rounded-2xl border-2 transition-all ${c.class} ${headerColor === c.id ? 'border-white ring-2 ring-pink-400' : 'border-transparent'}`} />
                    ))}
                  </div>
                </div>
              )}

              {/* Called Numbers Highlight Color */}
              {setUseSameColorForHighlight && setHighlightColor && (
                <div className="mb-6">
                  <label className="flex items-center gap-2 text-sm font-semibold text-pink-700 mb-2 cursor-pointer">
                    <input type="checkbox" checked={useSameColorForHighlight} onChange={e => setUseSameColorForHighlight(e.target.checked)} className="accent-pink-500" />
                    Use top row color for called numbers
                  </label>

                  {!useSameColorForHighlight && (
                    <div className="mt-3">
                      <div className="text-xs text-pink-600 mb-1.5">Called numbers highlight color</div>
                      <div className="flex gap-2">
                        {headerColors.map(c => (
                          <button
                            key={c.id}
                            onClick={() => setHighlightColor(c.id)}
                            className={`flex-1 h-9 rounded-2xl border-2 transition-all ${c.class} ${highlightColor === c.id ? 'border-white ring-2 ring-pink-400' : 'border-transparent'}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {setHideCalled && (
                <div className="mb-4">
                  <label className="flex items-center justify-between text-sm font-semibold text-pink-700 cursor-pointer">
                    <span>Hide already called numbers</span>
                    <input type="checkbox" checked={hideCalled} onChange={e => setHideCalled(e.target.checked)} className="accent-pink-500 w-5 h-5" />
                  </label>
                </div>
              )}

              {setOverlayFontSize && (
                <div className="mb-6">
                  <div className="flex justify-between text-sm font-semibold text-pink-700 mb-2">
                    <span>NEW NUMBER SIZE</span>
                    <span className="font-mono">{overlayFontSize}%</span>
                  </div>
                  <input type="range" min={45} max={90} value={overlayFontSize} onChange={e => setOverlayFontSize(parseInt(e.target.value))} className="w-full accent-pink-500" />
                </div>
              )}

              <div className="mb-6">
                <div className="flex justify-between text-sm font-semibold text-pink-700 mb-2">
                  <span>VOICE REPEATS</span>
                  <span className="font-mono">{voiceRepeats}</span>
                </div>
                <input type="range" min={1} max={5} value={voiceRepeats} onChange={e => setVoiceRepeats(parseInt(e.target.value))} className="w-full accent-pink-500" />
              </div>

              <div className="space-y-4 mb-6">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-pink-800">Sound Effects</span>
                  <input type="checkbox" checked={soundEnabled} onChange={e => setSoundEnabled(e.target.checked)} className="accent-pink-500 w-5 h-5" />
                </label>
              </div>

              {confirmReset ? (
                <div className="mb-4">
                  <p className="mb-2 text-center text-sm font-semibold text-red-700">
                    Reset the board? This cannot be undone.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmReset(false)}
                      className="flex-1 py-3 rounded-2xl bg-pink-100 text-pink-800 font-semibold hover:bg-pink-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onResetGame();
                        setConfirmReset(false);
                      }}
                      className="flex-1 py-3 rounded-2xl bg-red-500 text-white font-semibold hover:bg-red-600"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmReset(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-red-100 text-red-600 font-semibold hover:bg-red-200 active:bg-red-300 mb-4"
                >
                  <RotateCcw size={18} /> Reset Game
                </button>
              )}

              <button onClick={onClose} className="w-full py-3 rounded-3xl bg-blue-500 text-white font-bold text-lg bouncy-btn">Close</button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
